import { Button } from "@/components/ui/button";
import { useEffect, useMemo, useRef, useState } from "react";
import { SirenIcon } from "lucide-react";
import { isProRataDate } from "@/utils/is-pro-rata-date";
import { useCalculateInvoicingCompetency } from "@/hooks/useCalculateInvoicingCompetency";
import { simulatedPayment } from "../../context/use-booking-flow/use-booking-flow.types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatInTimeZone } from "date-fns-tz";
import { getUpsellBeginDate } from "./utils/get-upsell-begin-date";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { GetProRataCredits } from "../../adapters/get-pro-rata-credits/get-pro-rata-credits";
import { AdditionalPaymentsTable } from "../additional-payments-table";
import { UpsellCyclesTable } from "../updated-cycles-table";
import { BookingFlowHeader } from "../booking-flow-header";
import { JustDecimal } from "@/utils/format/regex/just-decimal";
import { Input } from "@/components/inputs/input";
import { usePoContractsUpdateFlow } from "../../context/use-po-contracts-update-flow/use-po-contracts-update-flow";
import {
  PoContractsUpsellFormData,
  PoContractsUpsellSchema,
} from "./po-contracts-upsell.schema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const PoContractsUpsell = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const [upsellOption, setUpsellOption] = useState(0);
  const [upsellProRataOption, setUpsellProRataOption] = useState(0);
  const {
    nextStep,
    poContractUpdateData,
    simulatedUpsell,
    SimulateUpsell,
    UpdateUpsellPayments,
    UpdateUpsellCycles,
    upsellData,
    setUpsellData,
    previousStep,
    selectedPackage,
    selectedCycle,
  } = usePoContractsUpdateFlow();

  const eligibleCycles = useMemo(() => {
    const versions = selectedPackage?.versions?.length
      ? selectedPackage.versions
      : selectedCycle
        ? [selectedCycle]
        : [];

    return versions.filter(
      (version) => version.status === "active" || version.status === "awaiting",
    );
  }, [selectedPackage, selectedCycle]);

  const [selectedStartCycleId, setSelectedStartCycleId] = useState<
    number | undefined
  >(
    () =>
      eligibleCycles.find((version) => version.status === "active")?.id ??
      eligibleCycles[0]?.id,
  );

  const startCycle = eligibleCycles.find(
    (version) => version.id === selectedStartCycleId,
  );

  const beginDate = useMemo(
    () =>
      poContractUpdateData
        ? getUpsellBeginDate(startCycle, poContractUpdateData.startDate)
        : "",
    [startCycle, poContractUpdateData],
  );

  const hasProRata = !!beginDate && isProRataDate(beginDate);

  const exceededPaymentDeadline =
    startCycle?.credits?.exceededPaymentDeadline ??
    selectedCycle?.credits?.exceededPaymentDeadline ??
    0;

  const { calculateInvoicingCompetency } = useCalculateInvoicingCompetency();
  const lastCompetencyPrefillKey = useRef("");

  useEffect(() => {
    const payments = simulatedUpsell?.newPayments;
    if (!payments?.length || !poContractUpdateData) return;

    const missing = payments.filter(
      (payment) => payment.paymentDate && !payment.invoicingCompetencyDate,
    );
    if (!missing.length) return;

    const prefillKey = missing.map((payment) => payment.paymentDate).join(",");
    if (lastCompetencyPrefillKey.current === prefillKey) return;
    lastCompetencyPrefillKey.current = prefillKey;

    const prefillCompetencies = async () => {
      const competencies = await calculateInvoicingCompetency(
        missing.map((payment) => payment.paymentDate),
        exceededPaymentDeadline,
      );
      if (!competencies.length) return;

      const competencyByDate = new Map(
        competencies.map((competency) => [
          competency.installmentDate,
          competency.invoicingCompetencyDate,
        ]),
      );

      UpdateUpsellPayments(
        payments.map((payment) =>
          payment.invoicingCompetencyDate ||
          !competencyByDate.has(payment.paymentDate)
            ? payment
            : {
                ...payment,
                invoicingCompetencyDate: competencyByDate.get(
                  payment.paymentDate,
                ),
              },
        ),
      );
    };

    prefillCompetencies();
  }, [simulatedUpsell?.newPayments]);

  const handleUpdatePayments = async (payments: simulatedPayment[]) => {
    const currentPayments = simulatedUpsell?.newPayments ?? [];
    const changedDates = payments
      .filter(
        (payment, index) =>
          payment.paymentDate &&
          payment.paymentDate !== currentPayments[index]?.paymentDate,
      )
      .map((payment) => payment.paymentDate);

    if (!changedDates.length || !poContractUpdateData) {
      UpdateUpsellPayments(payments);
      return;
    }

    const competencies = await calculateInvoicingCompetency(
      changedDates,
      exceededPaymentDeadline,
    );
    const competencyByDate = new Map(
      competencies.map((competency) => [
        competency.installmentDate,
        competency.invoicingCompetencyDate,
      ]),
    );

    UpdateUpsellPayments(
      payments.map((payment, index) =>
        payment.paymentDate !== currentPayments[index]?.paymentDate &&
        competencyByDate.has(payment.paymentDate)
          ? {
              ...payment,
              invoicingCompetencyDate: competencyByDate.get(
                payment.paymentDate,
              ),
            }
          : payment,
      ),
    );
  };

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm<PoContractsUpsellFormData>({
    resolver: zodResolver(PoContractsUpsellSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      upsellOption: upsellData?.upsellOption,
      upsellProRataOption: upsellData?.upsellProRataOption,
    },
  });

  const handleProRataOptionChange = (credits: number) => {
    if (simulatedUpsell && credits) {
      const { newPayments, updatedCycles } = simulatedUpsell;

      if (newPayments.length) {
        const updatedPayments = newPayments.map((payment) => {
          if (payment.proRata) {
            const creditUnitValue = payment.amount / payment.capacity;
            return {
              ...payment,
              amount: creditUnitValue * credits,
              capacity: credits,
            };
          }

          return payment;
        });

        setUpsellProRataOption(credits);
        UpdateUpsellPayments(updatedPayments);
      }

      const updatedCyclesData = updatedCycles.map((cycle) => {
        if (cycle.proRata) {
          const creditUnitValue = cycle.oldAmount / cycle.oldCapacity;
          return {
            ...cycle,
            newAmount: cycle.oldAmount + creditUnitValue * credits,
            newCapacity: credits + cycle.oldCapacity,
          };
        }

        return cycle;
      });

      UpdateUpsellCycles(updatedCyclesData);
    }
  };

  const calculateProRataCredits = async (
    startDate: string,
    credits: number,
  ) => {
    const proRataCredits = await GetProRataCredits(
      startDate,
      credits,
      poContractUpdateData!.company.id,
    );

    setUpsellProRataOption(proRataCredits);
    setValue("upsellProRataOption", proRataCredits);
  };

  useEffect(() => {
    if (upsellOption && poContractUpdateData) {
      SimulateUpsell({
        creditAmount: upsellOption,
        beginDate: beginDate,
        companyId: poContractUpdateData.company.id,
        packageId: poContractUpdateData.packageId,
      });

      hasProRata && calculateProRataCredits(beginDate, upsellOption);
    }

    if (!upsellOption && upsellData?.upsellOption) {
      setUpsellOption(upsellData.upsellOption);
    }
  }, [upsellOption, selectedStartCycleId]);

  const onSubmit = async (data: PoContractsUpsellFormData) => {
    try {
      setErrorMsg("");

      const missingCompetency = simulatedUpsell?.newPayments.some(
        (payment) => payment.paymentDate && !payment.invoicingCompetencyDate,
      );
      if (missingCompetency) {
        setErrorMsg(
          "Informe a competência de faturamento de todos os pagamentos.",
        );
        return;
      }

      setUpsellData({
        ...data,
        simulatedUpsellData: simulatedUpsell!,
        beginDate,
        startCycle: startCycle
          ? { id: startCycle.id, startDate: startCycle.startDate }
          : undefined,
      });
      nextStep();
    } catch (error) {
      setErrorMsg(
        error instanceof Error ? error.message : "Erro ao processar upsell",
      );
    }
  };

  return (
    <div className="bg-slate-50">
      <div className="p-6 max-w-6xl mx-auto bg-slate-50 h-full gap-8 flex flex-col">
        <BookingFlowHeader
          onClick={() => previousStep()}
          title="Editando contrato de venda de escritório"
          subtitle="Associe um escritório privativo à uma empresa contratante."
        />
        <form className="gap-8 flex flex-col" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex-col justify-start items-start gap-8 inline-flex">
            <Card className="w-full px-2">
              <CardHeader>
                {errorMsg && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{errorMsg}</p>
                  </div>
                )}

                <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                  Pagamentos adicionais
                </CardTitle>
                <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                  Informe quantos créditos serão adicionados, ajuste a data para
                  cada pagamento, e acompanhe as alterações em tempo real na
                  tabela abaixo.
                </CardDescription>
              </CardHeader>
              <CardContent className="gap-6 flex flex-col">
                {hasProRata && (
                  <div className="flex-col justify-start items-start gap-1 self-stretch flex">
                    <Alert className="bg-amber-50 rounded-lg border border-yellow-500/50 w-full flex-col justify-start items-start gap-1 inline-flex">
                      <div className="flex gap-3  items-center pl-6">
                        <div className="absolute top-4 left-4">
                          <SirenIcon
                            className="text-yellow-900"
                            width={16}
                            height={16}
                          />
                        </div>
                        <div>
                          <AlertTitle className="text-base font-medium text-yellow-900 mb-0">
                            Atenção: Pró-rata será aplicada!
                          </AlertTitle>
                          <AlertDescription className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                            Este booking possui uma data de início que não
                            coincide com o começo do mês.
                          </AlertDescription>
                          <h6 className="self-stretch text-yellow-900 text-sm font-normal leading-tight">
                            Os valores de repasse e créditos serão calculados
                            proporcionalmente aos dias restantes do mês atual.
                          </h6>
                        </div>
                      </div>
                    </Alert>
                  </div>
                )}
                <div className="self-stretch flex-col justify-start items-start gap-4 flex">
                  <div className="self-stretch justify-start items-start gap-4 inline-flex">
                    {eligibleCycles.length > 0 && (
                      <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                        <label className="block text-sm font-medium text-gray-700">
                          Ciclo de início das alterações
                        </label>
                        <div className="w-full">
                          <Select
                            value={selectedStartCycleId?.toString()}
                            onValueChange={(value) =>
                              setSelectedStartCycleId(Number(value))
                            }
                            disabled={eligibleCycles.length <= 1}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Selecione o ciclo" />
                            </SelectTrigger>
                            <SelectContent>
                              {eligibleCycles.map((cycle) => (
                                <SelectItem
                                  key={cycle.id}
                                  value={cycle.id.toString()}
                                >
                                  {`Ciclo ${cycle.id} — ${formatInTimeZone(
                                    cycle.startDate,
                                    "UTC",
                                    "dd/MM/yyyy",
                                  )} a ${formatInTimeZone(
                                    cycle.endDate,
                                    "UTC",
                                    "dd/MM/yyyy",
                                  )}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                      <label className="block text-sm font-medium text-gray-700">
                        Créditos adicionais
                      </label>
                      <div className="w-full">
                        <Controller
                          control={control}
                          name="upsellOption"
                          render={({ field, fieldState }) => (
                            <Input
                              {...field}
                              label="UpsellOption"
                              name="upsellOption"
                              type="text"
                              inputMode="decimal"
                              value={field.value || ""}
                              onChange={(event) => {
                                field.onChange({
                                  ...event,
                                  target: {
                                    ...event.target,
                                    value: JustDecimal(event.target.value),
                                  },
                                });
                              }}
                              onBlur={(event) => {
                                setUpsellOption(Number(event.target.value));
                              }}
                              required
                              error={
                                fieldState.error ? fieldState.error.message : ""
                              }
                            />
                          )}
                        />
                      </div>
                    </div>
                    {hasProRata && (
                      <div className="grow shrink basis-0 flex-col justify-start items-start gap-2 inline-flex">
                        <label className="block text-sm font-medium text-gray-700">
                          Créditos em pró-rata
                        </label>
                        <div className="w-full">
                          <Controller
                            control={control}
                            name="upsellProRataOption"
                            render={({ field, fieldState }) => (
                              <Input
                                {...field}
                                label="UpsellProRataOption"
                                name="upsellProRataOption"
                                type="text"
                                inputMode="decimal"
                                value={field.value || ""}
                                onChange={(event) => {
                                  field.onChange({
                                    ...event,
                                    target: {
                                      ...event.target,
                                      value: JustDecimal(event.target.value),
                                    },
                                  });
                                }}
                                onBlur={(event) => {
                                  handleProRataOptionChange(
                                    Number(event.target.value),
                                  );
                                }}
                                required
                                error={
                                  fieldState.error
                                    ? fieldState.error.message
                                    : ""
                                }
                                disable={true}
                              />
                            )}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {simulatedUpsell?.newPayments && (
                  <AdditionalPaymentsTable
                    data={simulatedUpsell.newPayments}
                    proRataCredits={upsellProRataOption}
                    UpdatePayments={handleUpdatePayments}
                    showInvoicingCompetency
                  />
                )}
              </CardContent>
            </Card>
            <Card className="w-full px-2">
              <CardHeader>
                <CardTitle className="self-stretch text-foreground text-2xl font-medium">
                  Ciclos afetados
                </CardTitle>
                <CardDescription className="self-stretch text-muted-foreground text-sm font-normal leading-tight">
                  {simulatedUpsell?.updatedCycles
                    ? "Revise abaixo como os ciclos ao longo da reserva serão impactados por ela."
                    : "Selecione um valor de créditos adicionais para mais informações."}
                </CardDescription>
              </CardHeader>
              {simulatedUpsell?.updatedCycles && (
                <CardContent>
                  <UpsellCyclesTable data={simulatedUpsell.updatedCycles} />
                </CardContent>
              )}
            </Card>
          </div>
          <div className="mt-8 flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={previousStep}
              className="px-4 py-2"
              disabled={isSubmitting}
            >
              Voltar
            </Button>
            <Button type="submit" className="px-4 py-2" disabled={isSubmitting}>
              {isSubmitting ? "Processando..." : "Avançar para revisão"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
